import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Modal from 'react-modal';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

Modal.setAppElement('#root');

const MACHINERY_GALLERY_IMAGES = [
  '/assets/machines/bobcat1.jpg',
  '/assets/machines/bobcat2.jpg',
  '/assets/machines/bobcat4.jpg',
];

const FLEET_COUNTS = [2, 1, 1, 6, 2, 3, 1, 1, 1, 2, 1];

/** Pairs first-half / second-half rows so each grid row stays visually aligned (two columns). */
function pairFleetRows(rows) {
  const mid = Math.ceil(rows.length / 2);
  return Array.from({ length: mid }, (_, i) => ({
    left: rows[i],
    right: rows[i + mid],
  }));
}

const Machines = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [photoIndex, setPhotoIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [fleetModalOpen, setFleetModalOpen] = useState(false);

  const fleetRows = useMemo(
    () => FLEET_COUNTS.map((count, i) => ({ count, labelKey: `machineryFleetRow${i}` })),
    []
  );

  const fleetPairs = useMemo(() => pairFleetRows(fleetRows), [fleetRows]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.key]);

  const handleImageClick = (index) => {
    setPhotoIndex(index);
    setLightboxOpen(true);
  };

  const renderFleetCell = (row) => (
    <div className="flex w-full min-w-0 items-start gap-3 rounded-xl px-1 py-2 sm:gap-3 sm:px-2">
      <span className="inline-flex min-w-[3.25rem] shrink-0 items-center justify-center rounded-lg bg-emerald-600/15 px-2.5 py-1 text-base font-black tabular-nums text-emerald-800 md:min-w-[3.5rem] md:text-lg">
        {row.count}×
      </span>
      <span className="min-w-0 flex-1 text-left text-base font-medium leading-snug text-slate-800 md:text-lg">
        {t(row.labelKey)}
      </span>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Infinity Energy Solar – Machines</title>
      </Helmet>
      <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-b from-green-100 via-yellow-100 to-blue-100">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url('/assets/background/machinery.png')`,
            backgroundSize: 'min(90vw, 1100px) auto',
            backgroundPosition: 'bottom right',
            backgroundRepeat: 'no-repeat',
            opacity: 0.28,
            zIndex: 1,
          }}
        />
        <div className="grid-pattern relative" style={{ zIndex: 2 }}>
          <section className="px-6 pb-24 pt-40 lg:px-16">
            <div className="mx-auto max-w-7xl">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-12 mx-auto max-w-4xl text-center"
              >
                <div className="mb-5 w-full px-2">
                  <h1 className="mx-auto max-w-full text-center text-2xl font-black tracking-tight text-blue-900 sm:text-3xl md:whitespace-nowrap md:text-4xl lg:text-5xl lg:leading-[1.2] md:leading-[1.25] leading-[1.35] py-1">
                    {t('machinesHero')}
                  </h1>
                </div>
                <p className="mx-auto max-w-3xl text-base leading-relaxed text-slate-700 md:text-lg">
                  {t('machinesIntro')}
                </p>
              </motion.div>

              <div id="machinery-gallery" className="scroll-mt-28">
                <div className="mb-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setFleetModalOpen(true)}
                    className="rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 px-8 py-3 text-base font-bold text-white shadow-lg transition hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 md:px-10 md:py-3.5 md:text-lg"
                  >
                    {t('machineryListButton')}
                  </button>
                </div>

                <h2 className="mb-4 text-center text-lg font-bold text-blue-900 md:text-xl">
                  {t('photoGallery')}
                </h2>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                  {MACHINERY_GALLERY_IMAGES.map((src, idx) => (
                    <motion.button
                      key={src}
                      type="button"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 + idx * 0.04 }}
                      whileHover={{ scale: 1.015 }}
                      whileTap={{ scale: 0.99 }}
                      className="group relative aspect-[4/3] w-full max-w-full overflow-hidden rounded-xl border border-slate-200/80 bg-slate-100/80 text-left shadow-sm ring-1 ring-black/[0.03] transition-shadow hover:border-emerald-300/60 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                      onClick={() => handleImageClick(idx)}
                    >
                      <img
                        src={src}
                        alt=""
                        className="h-full w-full object-cover transition duration-300 group-hover:brightness-[1.03]"
                      />
                      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/35 to-transparent py-6 opacity-0 transition group-hover:opacity-100" />
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Modal
        isOpen={fleetModalOpen}
        onRequestClose={() => setFleetModalOpen(false)}
        contentLabel={t('machineryFleetTitle')}
        className="relative mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl outline-none lg:max-w-4xl"
        overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-black/55 p-4"
      >
        <div className="relative flex shrink-0 items-center justify-center border-b border-slate-200 px-5 py-4 pr-14">
          <h2 className="text-center text-xl font-black text-blue-900 md:text-2xl">{t('machineryFleetTitle')}</h2>
          <button
            type="button"
            onClick={() => setFleetModalOpen(false)}
            className="absolute right-4 top-4 text-3xl font-bold leading-none text-slate-400 transition hover:text-blue-900 md:right-5 md:top-4"
            aria-label={t('machinesCloseModal')}
          >
            &times;
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-6 py-8 md:px-10 md:py-10">
            <ul className="mx-auto w-full max-w-3xl divide-y divide-slate-100 sm:hidden">
              {fleetRows.map((row) => (
                <li key={row.labelKey} className="py-3 first:pt-0 last:pb-0">
                  {renderFleetCell(row)}
                </li>
              ))}
            </ul>
            <ul className="mx-auto hidden w-full max-w-3xl divide-y divide-slate-100 sm:block lg:max-w-4xl">
              {fleetPairs.map((pair, i) => (
                <li
                  key={`${pair.left?.labelKey ?? 'l'}-${pair.right?.labelKey ?? 'r'}-${i}`}
                  className="grid grid-cols-2 items-start gap-x-8 gap-y-2 py-4 first:pt-0"
                >
                  <div className="min-w-0">{pair.left ? renderFleetCell(pair.left) : null}</div>
                  <div className="min-w-0">{pair.right ? renderFleetCell(pair.right) : null}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={photoIndex}
        slides={MACHINERY_GALLERY_IMAGES.map((src) => ({ src }))}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.92)' } }}
      />
    </>
  );
};

export default Machines;
